import type { UpgradeAccountState, UpgradeBillingInterval } from '@shared/core/analytics'
import { REMINDERS_PAUSED_REASONS } from '@shared/core/billing'
import type { BillingPlanDto, RemindersPausedReason } from '@shared/core/billing'
import type { GetProfileResponseDto } from '@shared/core/users'
import { billingTypes } from 'src/modules/payment/constants/payment.constants'
import {
  PADDLE_CURRENCY_CODES,
  PLAN_BUTTON_LABEL_IDS,
  PLAN_USER_SEATS_BY_REMINDERS_LIMIT,
} from 'src/modules/payment/constants/plans-offer.constants'
import type { PaddlePlanData } from 'src/modules/payment/hooks/use-paddle-plan-data'
import type { PlanButtonLabelId, ReminderCycleWindow } from 'src/modules/payment/types/plans-offer.types'
import { getNextResetDate } from 'src/modules/user/components/dashboard-wrapper/account-snapshot/account-snapshot.utils'

const MONTH_FALLBACK_MS = 30 * 24 * 60 * 60 * 1000

export function getPlanBillingInterval(plan: Pick<BillingPlanDto, 'billing_type'>): UpgradeBillingInterval {
  return plan.billing_type === billingTypes.longInterval ? 'year' : 'month'
}

export function getUpgradeAccountState(options: {
  isFreeTrial: boolean
  isPaused: boolean
  pausedReason: RemindersPausedReason | null
}): UpgradeAccountState {
  // Ended accounts resolve to the free plan, so this must win over the free-trial branch.
  if (options.pausedReason === REMINDERS_PAUSED_REASONS.subscriptionEnded) {
    return 'subscription_ended'
  }

  if (!options.isFreeTrial) {
    return 'paid'
  }

  return options.isPaused ? 'free_exhausted' : 'free_active'
}

export function getPlanButtonLabelId(options: {
  isFreeTrial: boolean
  isPaused: boolean
  isCurrentPlan: boolean
}): PlanButtonLabelId {
  if (options.isFreeTrial) {
    return options.isPaused ? PLAN_BUTTON_LABEL_IDS.resume : PLAN_BUTTON_LABEL_IDS.subscribe
  }

  return options.isCurrentPlan ? PLAN_BUTTON_LABEL_IDS.currentPlan : PLAN_BUTTON_LABEL_IDS.upgrade
}

export function getPlanUserSeats(plan: Pick<BillingPlanDto, 'reminders_limit'>): number | undefined {
  return PLAN_USER_SEATS_BY_REMINDERS_LIMIT[plan.reminders_limit ?? -1]
}

/** The UTC window the sent counter covers; without a cycle anchor, the last 30 days. */
export function getReminderCycleWindow(profile: GetProfileResponseDto | undefined, now: Date): ReminderCycleWindow {
  const end = getNextResetDate(profile, now)

  if (!end) {
    return { start: new Date(now.getTime() - MONTH_FALLBACK_MS), end: now }
  }

  const year = end.getUTCFullYear()
  const previousMonth = end.getUTCMonth() - 1
  const lastDayOfPreviousMonth = new Date(Date.UTC(year, previousMonth + 1, 0)).getUTCDate()
  const startDay = Math.min(profile.billing_cycle_day, lastDayOfPreviousMonth)

  return { start: new Date(Date.UTC(year, previousMonth, startDay)), end }
}

/** ISO code of the currency a Paddle-localized price is shown in, or its raw symbol when unrecognised. */
export function getPaddlePriceCurrencyLabel(data: PaddlePlanData | undefined): string | undefined {
  const symbol = data?.recurring?.price?.net?.match(/^[^\d,.]+/)?.[0]?.trim()

  if (!symbol) {
    return undefined
  }

  return getCodesBySymbol().get(symbol) ?? symbol
}

let codesBySymbol: Map<string, string> | undefined

/** Built once: every code costs two `Intl.NumberFormat`, and this is called from a render body. */
function getCodesBySymbol(): Map<string, string> {
  if (!codesBySymbol) {
    codesBySymbol = new Map()

    for (const code of PADDLE_CURRENCY_CODES) {
      // First code wins, so lookups keep the order the previous `.find` used.
      for (const key of [code, ...getCurrencySymbols(code)]) {
        if (!codesBySymbol.has(key)) codesBySymbol.set(key, code)
      }
    }
  }

  return codesBySymbol
}

function getCurrencySymbols(code: string): string[] {
  return [`${code.slice(0, 2)}$`, readCurrencySymbol(code, 'en-US'), readCurrencySymbol(code, 'en-GB')]
}

function readCurrencySymbol(code: string, locale: string): string {
  const parts = new Intl.NumberFormat(locale, { style: 'currency', currency: code }).formatToParts(0)

  return parts.find((part) => part.type === 'currency')?.value ?? code
}
